import GuruPayIcon from '@assets/GuruPayIcon.svg?react';
import { Button, FileIcons, Typography } from '@gurupay/gp-ui-library';
import type { FileProps, MessageContent } from '@hooks/useMessage';
import { getTime } from '@utils/utils';
import clsx from 'clsx';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

interface ChatMessageProps {
	content: MessageContent;
	isSelf: boolean;
	createdAt: string;
	files?: FileProps[];
}

const formatContent = (data: MessageContent, t: TFunction<'translation', undefined>) => {
	return Object.entries(data).map(([key, value]) => (
		<Typography key={key} className="whitespace-pre-wrap mb-space-8">
			<span className="font-bold">{t(`content.${key}`)}:</span> {value ?? ''}
		</Typography>
	));
};

const ChatMessage = ({ content, isSelf, createdAt, files }: ChatMessageProps) => {
	const { t } = useTranslation('', { keyPrefix: 'messages' });
	const time = getTime(createdAt);
	const displayContent = formatContent(content, t);
	const displayAttachements = isSelf && files && files?.length > 0;

	return (
		<div
			className={clsx(
				'flex w-full mb-space-16',
				isSelf ? 'items-end justify-self-end flex-col' : 'items-start flex-row',
			)}
		>
			{!isSelf && (
				<GuruPayIcon className="self-end mr-space-8 w-[40px] h-[40px] flex-shrink-0" />
			)}

			<div
				className={clsx(
					'text-base-medium min-h-[99px] w-max max-w-[230px] sm:max-w-fit h-max rounded-2xl  p-space-16 pb-space-8 flex flex-col justify-between',
					isSelf
						? 'bg-neutral-800 text-neutral-50 rounded-br-none'
						: 'bg-neutral-50 text-neutral-950 rounded-bl-none',
				)}
			>
				{displayContent}

				<small
					className={clsx(
						'block text-xs-regular mt-space-8 text-right',
						isSelf ? 'text-neutral-200' : 'text-neutral-600',
					)}
				>
					{time}
				</small>
			</div>

			{displayAttachements && (
				<div>
					{files.map((file) => {
						const isExpired = new Date(file.download.expires_at).getTime() <= Date.now();
						return (
							<Button
								key={file.id}
								className="font-bold mt-space-8 flex-row-reverse"
								disabled={isExpired}
								icon={<FileIcons />}
								onClick={() => {
									window.location.href = file.download.url;
								}}
								size="sm"
								variant="secondary"
							>
								{file.filename}
							</Button>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default ChatMessage;
